import { REJECT_REASON_GROUPS } from '@/constants/reject-reasons';
import { Icon, Flex } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { http } from '@/lib/ats-https';
import { http as httpApi } from '@/lib/http';
import { RejectEmailPreview } from './RejectEmailPreview';
import { Vector } from '@/assets/icons/tasks';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';
import useAtsProjectPermissions from '@/hooks/useAtsProjectPermissions';
import { KText } from '@/ui-library/components/text';
import { KGroupHeading } from '@/ui-library/components/organisms/KFields/KGroupHeading';
import {
  CustomOption,
  CustomSingleValue,
} from './rejectEmailTemplateSelectComponents';
import './customDropdown.scss';

const groupOptions = (options) => {
  const groups = {};

  // Group options based on the "group" property
  options?.forEach((option) => {
    const groupName = option.group;
    if (!groups[groupName]) {
      groups[groupName] = [];
    }
    // Only add enabled and not deleted items, and explicitly exclude "Below AI score threshold"
    if (
      option?.enabled &&
      !option?.deleted &&
      option?.reason !== 'Below AI score threshold'
    )
      groups[groupName].push(option);
  });

  // Convert the grouped options into the desired format and filter out empty groups
  const groupedOptions = Object.entries(groups)
    .filter(([_, groupOptions]) => (groupOptions as any[]).length > 0) // Filter out empty groups
    .map(([label, groupOptions]) => ({
      label: REJECT_REASON_GROUPS?.find((rejRsn) => rejRsn?.id === label)
        ?.label,
      options: groupOptions,
    }));

  return groupedOptions;
};

export const useRejectEmailFormConfig = ({
  formRef = null,
  isProjectCreationAllowed = true,
  scrollToProject = true,
  isQuickAppReview = false,
  atsJobStageId = undefined,
  lazyFetchTemplates = false,
} = {}) => {
  //@ts-expect-error usePermissionGuard is untyped JS
  const { isFlowEnabled } = usePermissionGuard();

  const { canCreateProject, showAddToProjectInContacts } =
    useAtsProjectPermissions();

  const [isFormValid, setIsFormValid] = useState(false);
  const queryClient = useQueryClient();
  const queryKey = ['emailTemplates', 'rejection'];
  const staleTime = 5 * 60 * 1000; // 5 minutes
  const gcTime = 30 * 60 * 1000; // 30 minutes

  const fetchEmailTemplatesQuery = async () => {
    const response = await http({
      method: 'get',
      url: '/ats_comm_templates',
      params: {
        filter_by: {
          category: ['candidate_application_rejected'],
        },
      },
    });
    // Extract data array from response (matching RTK Query structure)
    return response?.data || [];
  };

  const { data: templateOptions = [] } = useQuery({
    queryKey,
    queryFn: fetchEmailTemplatesQuery,
    enabled: !lazyFetchTemplates, // Auto-fetch if not lazy
    staleTime,
    gcTime,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Wrapper function that only refetches if cache is stale or missing
  const fetchEmailTemplates = () => {
    const queryState = queryClient.getQueryState(queryKey);
    const now = Date.now();
    const dataUpdatedAt = queryState?.dataUpdatedAt || 0;
    const isStale = !queryState?.data || now - dataUpdatedAt > staleTime;

    if (isStale) {
      queryClient.fetchQuery({
        queryKey,
        queryFn: fetchEmailTemplatesQuery,
        staleTime,
      });
    }
  };

  const fetchRejectReasons = async () => {
    const response = await http({
      method: 'get',
      url: '/ats_rejection_reasons',
    });
    // Match RTK Query transformResponse structure: response?.data
    return response?.data || [];
  };

  const { data: rejectReasons = [] } = useQuery({
    queryKey: ['atsRejectionReasons'],
    queryFn: fetchRejectReasons,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const fetchProjectList = async () => {
    const response = await httpApi.get('/projects/list');
    // Match RTK Query transformResponse structure: response?.data?.projects
    return response?.data?.projects || [];
  };

  const { data: projectListData, refetch: refetchProjectListData } = useQuery({
    queryKey: ['projects', 'list'],
    queryFn: fetchProjectList,
    staleTime: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);

  const setCreatedProjectData = async (data) => {
    await refetchProjectListData();
    formRef?.current?.setValues((prev) => ({
      ...prev,
      selectedProject: data?.id,
      addToProject: true,
    }));
  };

  const getProjectFields = () => {
    const fields =
      isFlowEnabled && showAddToProjectInContacts()
        ? [
            {
              name: 'addToProject',
              label: 'Add to a project',
              type: 'checkbox',
              style: { position: 'relative', bottom: '10px' },
              onCheckedChange: (value) => {
                if (value && scrollToProject) {
                  setTimeout(() => {
                    const labelElements =
                      document.getElementsByTagName('label');
                    Array.from(labelElements).forEach((labelElement) => {
                      if (
                        labelElement.getAttribute('for') === 'selectedProject'
                      ) {
                        labelElement.scrollIntoView({
                          behavior: 'smooth',
                          block: 'center',
                        });
                      }
                    });
                  }, 0);
                }
              },
            },
            {
              name: 'selectedProject',
              type: 'select',
              label: 'Select a project',
              placeholder: 'Select a project',
              labelKey: 'name',
              isConditional: true,
              isAppendToBody: true,
              isMulti: false,
              labelStyles: { position: 'relative', bottom: '20px' },
              newStyles: [
                {
                  type: 'control',
                  newStyles: {
                    position: 'relative',
                    bottom: '20px',
                  },
                },
              ],
              conditionConfig: { field: 'addToProject', value: true },
              validationType: 'object',
              errorMessage: 'Project is required',
              options: projectListData,
              valueKey: 'id',
              id: 'selectedproject',
            },
          ]
        : [];

    // Add the custom field if `createProjectPermision` is true
    if (canCreateProject() && isProjectCreationAllowed && isFlowEnabled) {
      fields.push({
        type: 'custom',
        //@ts-expect-error fields array is loosely typed; Field shape varies per entry
        Field: () => (
          <Flex position={'relative'} bottom="10px">
            <KText size="sm-semi-bold">
              Can&apos;t find what you&apos;re looking for?{' '}
            </KText>
            <KText
              color={'ats-primary'}
              size="sm-semi-bold"
              onClick={() => {
                setIsCreateProjectOpen(true);
              }}
              cursor="pointer"
              ml="2px"
            >
              Create a project
              <Icon as={Vector} ml="4px" />
            </KText>
          </Flex>
        ),
      });
    }

    return fields;
  };

  const rejectFormConfig = ({
    contact = {},
    initialValues = null,
    onCustomChangeForRejectReason = () => {},
  } = {}) => [
    {
      name: 'ats_rejection_reason_id',
      label: 'Rejection reason',
      placeholder: 'Select rejection reason',
      errorMessage: 'Reason is required',
      type: 'select',
      isAppendToBody: true,
      components: {
        GroupHeading: KGroupHeading,
      },
      isRequired: true,
      options: groupOptions(rejectReasons),
      valueKey: 'id',
      labelKey: 'reason',
      containerStyles: {},
      customOnChange: onCustomChangeForRejectReason,
      styleVersion: 'v2',
    },
    !initialValues?.skipEmail && {
      name: 'sendRejectEmail',
      label: 'Send email to candidate',
      type: 'checkbox',
      isEditable: isQuickAppReview || !initialValues?.sendRejectEmail,
    },
    {
      name: 'selectedTemplate',
      type: 'autoComplete',
      label: 'Select reject email template',
      placeholder: 'Select email template',
      labelKey: 'name',
      isConditional: true,
      useInput: true,
      styleVersion: 'v2',
      menuPortalTarget: document.body,
      options: templateOptions?.map((opt) => ({
        label: opt.name,
        value: opt,
        ...opt,
      })),
      pluckById: true,
      updateEndpointOnChange: true,
      loadDefaultOptions: true,
      customStyles: {
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      },
      conditionConfig: { field: 'sendRejectEmail', value: true },
      errorMessage: 'Email template is required',
      fetchConfig: {
        method: 'get',
        endPoint: `/ats_comm_templates?filter_by[category][]=candidate_application_rejected&items=200&filter_by[name]=`,
        processResponse: (res) => {
          const options = res?.data?.map((item) => ({
            label: item?.name,
            value: item,
            ...item,
          }));

          return options;
        },
      },
      components: {
        Option: CustomOption,
        SingleValue: CustomSingleValue,
      },
    },
    {
      type: 'custom',
      Field: RejectEmailPreview,
      props: {
        contact,
        templateOptions,
        initialValues,
        isQuickAppReview,
        atsJobStageId,
      },
    },
    {
      type: 'custom',
      Field: ({ formik }) => {
        useEffect(() => {
          setIsFormValid(formik?.isValid && formik?.dirty);
        }, [formik]);
      },
    },
    {
      type: 'custom',
      name: 'rejectEmailConfig',
      Field: () => <></>,
    },
    ...getProjectFields(),
  ];

  const handleModalClose = () => {
    setIsCreateProjectOpen(false);
  };

  return {
    rejectFormConfig,
    templateOptions,
    isCreateProjectOpen,
    setIsCreateProjectOpen,
    handleModalClose,
    setCreatedProjectData,
    isFormValid,
    fetchEmailTemplates,
  };
};
